import { useEffect, useState, useMemo, useCallback } from "react";
import { CirclesWithBar } from "react-loader-spinner";
import { LoadingOverlay } from "@achmadk/react-loading-overlay";
import { useSnackbar } from "notistack";
import {
  getPaymentV2FailedTransaction,
  rescheduleAllTransactions,
  processSingleTransaction,
} from "./paymentV2DetailsService";
import { ITransaction, IQueueTransactionSummary } from "../lib";
import TransactionTable from "./paymentV2Table";
import styled from "styled-components";
import DisplayCard from "../shared/displayCard";
import { SummaryContainer } from "../shared/summaryContainer";

const StyledH3 = styled.h3`
  margin: 30px 0px 30px 5%;
`;

const StyledDiv = styled.div`
  display: flex;
  justify-content: space-between;
`;

const StyledButton = styled.button`
  margin: 25px 5% 25px 0px;
`;

const StyledFooter = styled.div`
  display: flex;
  flex-direction: row-reverse;
  margin: 30px 5% 30px 0px;
  button {
    height: 35px;
  }
  #previousButton {
    margin-right: 15px;
  }
`;

export const PaymentV2Details = (): JSX.Element => {
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasLoadingError, setHasLoadingError] = useState(false);
  const [transactions, setTransactions] = useState<ITransaction[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [queueTransactionSummary, setQueueTransactionSummary] =
    useState<IQueueTransactionSummary>({
      totalQueueCount: 0,
      totalLess12QueueCount: 0,
      totalLess24QueueCount: 0,
      totalLess48QueueCount: 0,
      totalMore48QueueCount: 0,
    });
  const [reloading, setReloading] = useState(1);

  useEffect(() => {
    const getTransaction = async () => {
      const {
        totalFailedCount: tCount,
        transactions: failedTransactions,
        totalQueueCount,
        totalLess12QueueCount,
        totalLess24QueueCount,
        totalLess48QueueCount,
        totalMore48QueueCount,
        success,
        errorMsg,
      } = await getPaymentV2FailedTransaction(page);
      if (success) {
        setTransactions(failedTransactions || []);
        setTotalCount(tCount || 0);
        setQueueTransactionSummary({
          totalQueueCount: totalQueueCount || 0,
          totalLess12QueueCount: totalLess12QueueCount || 0,
          totalLess24QueueCount: totalLess24QueueCount || 0,
          totalLess48QueueCount: totalLess48QueueCount || 0,
          totalMore48QueueCount: totalMore48QueueCount || 0,
        });
      } else {
        setHasLoadingError(true);
        enqueueSnackbar(errorMsg, {
          variant: "error",
        });
      }
      setLoading(false);
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "smooth",
      });
    };
    getTransaction();
    // clean up when component unmounts
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, reloading]);

  useEffect(() => {
    const intervalRefresh = setInterval(() => {
      setLoading(true);
      setReloading(reloading + 1);
    }, 30000);
    return () => clearInterval(intervalRefresh);
  }, [reloading]);

  const displayRange = useMemo(() => {
    return {
      start: (page - 1) * 50 + (transactions.length === 0 ? 0 : 1),
      end: (page - 1) * 50 + transactions.length,
    };
  }, [page, transactions]);

  const handlePagenation = useCallback(
    (button: string) => () => {
      setLoading(true);
      if (button === "next") {
        setPage(page + 1);
      } else {
        setPage(page - 1);
      }
    },
    [page]
  );

  const handleRescheduleAllTransactions = useCallback(async () => {
    setLoading(true);
    const { success, errorMsg, count } = await rescheduleAllTransactions();
    enqueueSnackbar(
      success
        ? `${count} Payments have been successfully scheduled for retry`
        : errorMsg,
      {
        variant: success ? "success" : "error",
      }
    );
    setPage(1);
    setReloading(reloading + 1);
  }, [enqueueSnackbar, reloading]);

  const processSingleTransactionAction = useCallback(
    async (transactionId: string, action: string) => {
      setLoading(true);
      const { success, errorMessage } = await processSingleTransaction(
        transactionId,
        action
      );
      enqueueSnackbar(
        success
          ? `Transaction ${transactionId} has been ${
              action === "remove" ? "removed" : "rescheduled"
            } successfully`
          : errorMessage,
        {
          variant: success ? "success" : "error",
        }
      );
      if (!success) {
        setLoading(false);
      } else {
        setReloading(reloading + 1);
      }
    },
    [enqueueSnackbar, reloading]
  );

  return (
    <LoadingOverlay
      active={loading}
      spinner={
        <CirclesWithBar
          height="100"
          width="100"
          color="#4fa94d"
          visible={loading}
          ariaLabel="circles-with-bar-loading"
          wrapperStyle={{ top: "250px", position: "fixed" }}
        />
      }
    >
      {hasLoadingError ? (
        <h3>Error while fetching exceeded PaymentV2 transaction</h3>
      ) : (
        <>
          <SummaryContainer marginLeft="5%">
            <DisplayCard
              header="Total Payment in QUEUE"
              count={queueTransactionSummary.totalQueueCount}
            />
            <DisplayCard header="Total Failed Payment" count={totalCount} />
          </SummaryContainer>
          <SummaryContainer marginLeft="5%">
            <DisplayCard
              header="Payment in QUEUE 0 - 12 hours"
              count={queueTransactionSummary.totalLess12QueueCount}
            />
            <DisplayCard
              header="Payment in QUEUE 12 - 24 hours"
              count={queueTransactionSummary.totalLess24QueueCount}
            />
            <DisplayCard
              header="Payment in QUEUE 24 - 48 hours"
              count={queueTransactionSummary.totalLess48QueueCount}
            />
            <DisplayCard
              header="Payment in QUEUE > 48 hours"
              count={queueTransactionSummary.totalMore48QueueCount}
            />
          </SummaryContainer>
          <StyledDiv>
            <StyledH3>
              Displaying {displayRange.start} - {displayRange.end} out of{" "}
              {totalCount + queueTransactionSummary.totalMore48QueueCount}{" "}
              failures
            </StyledH3>
            <StyledButton
              onClick={handleRescheduleAllTransactions}
              disabled={transactions.length === 0}
            >
              Reschedule All
            </StyledButton>
          </StyledDiv>
          <TransactionTable
            data={transactions}
            page={page}
            handleSingleTransactionAction={processSingleTransactionAction}
          />
          <StyledFooter>
            <button
              onClick={handlePagenation("next")}
              disabled={
                50 * (page - 1) + transactions.length ===
                totalCount + queueTransactionSummary.totalMore48QueueCount
              }
            >
              Next page
            </button>
            <button
              onClick={handlePagenation("previous")}
              id="previousButton"
              disabled={page === 1}
            >
              Previous page
            </button>
          </StyledFooter>
        </>
      )}
    </LoadingOverlay>
  );
};
